import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { formatEuro } from "@/lib/api";
import { useWatchlist } from "@/context/WatchlistContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import PdfButton from "@/components/PdfButton";
import { Star, House, Gavel, Trash, TrendUp, MapPin, Ruler, Coins, ChartLine, ArrowRight, Bed, Bathtub } from "@phosphor-icons/react";
import ServiceIcon from "@/components/ServiceIcon";

export default function Preferiti() {
  const { items, removeById, refresh } = useWatchlist();
  useEffect(() => { refresh(); }, [refresh]);

  const listings = useMemo(() => items.filter((x) => x.item_kind === "listing" && x.snapshot), [items]);
  const auctions = useMemo(() => items.filter((x) => x.item_kind === "auction" && x.snapshot), [items]);
  const orphans = useMemo(() => items.filter((x) => !x.snapshot), [items]);

  // Portfolio KPIs (aggregated across saved sale listings)
  const kpis = useMemo(() => {
    const saleItems = listings.filter((x) => x.snapshot?.type === "vendita" && x.financials);
    if (saleItems.length === 0) return null;
    const totalPrice = saleItems.reduce((s, x) => s + (x.snapshot.price || 0), 0);
    const totalRent = saleItems.reduce((s, x) => s + ((x.financials.est_monthly_rent || 0) * 12), 0);
    const totalDown = saleItems.reduce((s, x) => s + (x.financials.down_payment || 0), 0);
    const totalCashflow = saleItems.reduce((s, x) => s + (x.financials.net_annual_cashflow || 0), 0);
    const avgYield = saleItems.reduce((s, x) => s + (x.financials.gross_yield_pct || 0), 0) / saleItems.length;
    const avgCoc = saleItems.reduce((s, x) => s + (x.financials.coc_return_pct || 0), 0) / saleItems.length;
    return {
      count: saleItems.length,
      totalPrice, totalRent, totalDown, totalCashflow,
      avgYield: Math.round(avgYield * 100) / 100,
      avgCoc: Math.round(avgCoc * 100) / 100,
    };
  }, [listings]);

  return (
    <div className="min-h-[calc(100vh-56px)] bg-zinc-950">
      <div className="border-b border-white/10 p-6">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <ServiceIcon name="tracking" size={64} withGlow />
          <div>
            <div className="font-mono-data text-xs text-lime-400 tracking-widest mb-2">— PREFERITI · INVESTMENT TRACKING</div>
            <h1 className="font-display font-black text-3xl">La tua collezione.</h1>
            <p className="text-sm text-zinc-400 mt-1">
              {items.length} elementi salvati · {listings.length} immobili · {auctions.length} aste
            </p>
          </div>
        </div>
      </div>

      {kpis && (
        <div className="max-w-7xl mx-auto px-6 pt-6">
          <PortfolioSummary kpis={kpis} />
        </div>
      )}

      <div className="max-w-7xl mx-auto p-6">
        <Tabs defaultValue="all" className="w-full">
          <TabsList className="rounded-none bg-zinc-900 border border-white/10 p-0 h-11">
            <TabsTrigger value="all" className="rounded-none data-[state=active]:bg-lime-400 data-[state=active]:text-lime-950 px-5 font-mono-data text-xs" data-testid="tab-all">
              TUTTI ({items.length})
            </TabsTrigger>
            <TabsTrigger value="listings" className="rounded-none data-[state=active]:bg-lime-400 data-[state=active]:text-lime-950 px-5 font-mono-data text-xs" data-testid="tab-listings">
              <House size={12} weight="bold" className="mr-1.5" /> IMMOBILI ({listings.length})
            </TabsTrigger>
            <TabsTrigger value="auctions" className="rounded-none data-[state=active]:bg-amber-400 data-[state=active]:text-amber-950 px-5 font-mono-data text-xs" data-testid="tab-auctions">
              <Gavel size={12} weight="bold" className="mr-1.5" /> ASTE ({auctions.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="mt-6">
            {items.length === 0 ? (
              <EmptyState />
            ) : (
              <div className="space-y-6">
                {listings.length > 0 && <ListingsGrid items={listings} onRemove={removeById} />}
                {auctions.length > 0 && <AuctionsTable items={auctions} onRemove={removeById} />}
              </div>
            )}
          </TabsContent>
          <TabsContent value="listings" className="mt-6">
            {listings.length === 0 ? <EmptyState label="Nessun immobile salvato" /> : <ListingsGrid items={listings} onRemove={removeById} />}
          </TabsContent>
          <TabsContent value="auctions" className="mt-6">
            {auctions.length === 0 ? <EmptyState label="Nessuna asta salvata" /> : <AuctionsTable items={auctions} onRemove={removeById} />}
          </TabsContent>
        </Tabs>

        {orphans.length > 0 && (
          <div className="mt-8 border border-white/10 p-4 text-sm text-zinc-500 font-mono-data">
            {orphans.length} elemento/i preferito/i non più disponibile/i.
            <Button variant="ghost" size="sm" className="ml-3 text-red-400" onClick={() => orphans.forEach((x) => removeById(x.id))}>
              Rimuovi tutti
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function PortfolioSummary({ kpis }) {
  const cashflowPositive = kpis.totalCashflow >= 0;
  return (
    <div className="border border-white/10 bg-gradient-to-br from-lime-400/[0.03] to-transparent p-5" data-testid="portfolio-summary">
      <div className="flex items-center gap-2 mb-4">
        <ChartLine size={16} weight="bold" className="text-lime-400" />
        <span className="font-mono-data text-xs text-lime-400 tracking-widest">RIEPILOGO PORTFOLIO · {kpis.count} IMMOBILI</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        <Kpi label="INVESTIMENTO" value={formatEuro(kpis.totalPrice)} color="text-zinc-100" />
        <Kpi label="ANTICIPO" value={formatEuro(kpis.totalDown)} color="text-zinc-100" hint="80% LTV" />
        <Kpi label="CANONE ANNUO" value={formatEuro(kpis.totalRent)} color="text-cyan-400" />
        <Kpi label="CASH FLOW" value={formatEuro(kpis.totalCashflow)} color={cashflowPositive ? "text-lime-400" : "text-red-400"} hint="annuo netto" />
        <Kpi label="YIELD MEDIO" value={`${kpis.avgYield}%`} color="text-lime-400" hint="lordo" />
        <Kpi label="COC MEDIO" value={`${kpis.avgCoc}%`} color="text-lime-400" hint="cash-on-cash" />
      </div>
    </div>
  );
}

function Kpi({ label, value, color = "text-zinc-100", hint = null }) {
  return (
    <div>
      <div className="font-mono-data text-[10px] text-zinc-500 tracking-widest">{label}</div>
      <div className={`font-mono-data text-xl font-bold mt-1 ${color}`}>{value}</div>
      {hint && <div className="font-mono-data text-[10px] text-zinc-600 mt-0.5">{hint}</div>}
    </div>
  );
}

function EmptyState({ label = "Nessun preferito ancora." }) {
  return (
    <div className="border border-dashed border-white/10 p-16 text-center">
      <Star size={40} weight="bold" className="mx-auto text-zinc-700 mb-4" />
      <div className="font-mono-data text-xs text-zinc-500 mb-2">{label}</div>
      <div className="text-sm text-zinc-400">Salva immobili e aste dalle pagine <span className="text-lime-400">Inserzioni</span>, <span className="text-amber-400">Aste</span> e <span className="text-lime-400">Ricerca ideale</span>.</div>
    </div>
  );
}

function yieldColor(y) {
  if (y == null) return "text-zinc-400";
  if (y < 3.5) return "text-zinc-300";
  if (y < 5) return "text-cyan-400";
  if (y < 7) return "text-lime-400";
  return "text-amber-400"; // very high yield = potentially risky
}

function ListingsGrid({ items, onRemove }) {
  return (
    <div>
      <div className="font-mono-data text-xs text-lime-400 tracking-widest mb-3 flex items-center gap-2">
        <House size={14} weight="bold" /> IMMOBILI SALVATI
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fav-listings">
        {items.map((x) => {
          const l = x.snapshot;
          const f = x.financials;
          const isSale = l.type === "vendita";
          const yPct = f?.gross_yield_pct;
          const capPct = f?.cap_rate_pct;
          const cocPct = f?.coc_return_pct;
          const monthlyRent = f?.est_monthly_rent;
          const pricePerSqm = l.price_per_sqm || (l.sqm ? Math.round(l.price / l.sqm) : null);

          return (
            <div
              key={x.id}
              className="border border-white/10 bg-zinc-900/40 hover:border-lime-400/40 hover:bg-zinc-900/70 transition-all flex flex-col group overflow-hidden"
              data-testid={`fav-listing-${l.id}`}
            >
              {/* Photo */}
              {l.photo_url && (
                <div className="relative w-full aspect-[16/10] bg-zinc-900 overflow-hidden border-b border-white/5">
                  <img
                    src={l.photo_url}
                    alt={l.title}
                    className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-500"
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                  {/* overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent pointer-events-none" />
                  {/* Price overlay */}
                  <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between">
                    <div className="font-mono-data text-lg font-black text-zinc-50 drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">
                      {formatEuro(l.price)}
                      {!isSale && <span className="text-xs text-zinc-300 font-normal">/mese</span>}
                    </div>
                    {isSale && yPct != null && (
                      <div className={`font-mono-data text-xs font-bold px-2 py-0.5 backdrop-blur-sm ${
                        yPct >= 6 ? "bg-amber-400/90 text-amber-950" : yPct >= 4 ? "bg-lime-400/90 text-lime-950" : "bg-cyan-400/90 text-cyan-950"
                      }`}>
                        {yPct}%
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Header */}
              <div className="p-4 border-b border-white/5">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={`rounded-none font-mono-data text-[10px] font-bold ${isSale ? "bg-lime-400 text-lime-950 hover:bg-lime-400" : "bg-cyan-500 text-cyan-950 hover:bg-cyan-500"}`}>
                    {l.type.toUpperCase()}
                  </Badge>
                  <Badge className="rounded-none bg-zinc-800 text-zinc-400 font-mono-data text-[9px] hover:bg-zinc-800">{l.source}</Badge>
                  {!l.photo_url && isSale && yPct != null && (
                    <Badge className={`rounded-none bg-transparent border font-mono-data text-[10px] font-bold ml-auto hover:bg-transparent ${
                      yPct >= 6 ? "border-amber-400/50 text-amber-400" : yPct >= 4 ? "border-lime-400/50 text-lime-400" : "border-cyan-400/50 text-cyan-400"
                    }`}>
                      {yPct}%
                    </Badge>
                  )}
                </div>
                <div className="font-display font-bold text-sm leading-tight mb-1 truncate" title={l.title}>{l.title}</div>
                <div className="text-[11px] text-zinc-500 flex items-center gap-1 truncate">
                  <MapPin size={10} weight="bold" className="shrink-0" />
                  <span className="truncate">{l.zone} · {l.address}</span>
                </div>
              </div>

              {/* Big price (only when no photo, otherwise price is overlaid) */}
              {!l.photo_url && (
                <div className="px-4 pt-4 pb-3">
                  <div className="font-mono-data text-2xl font-black text-zinc-50 leading-none">
                    {formatEuro(l.price)}
                    {!isSale && <span className="text-xs text-zinc-500 font-normal">/mese</span>}
                  </div>
                  <div className="flex items-center gap-3 mt-1.5 text-[11px] font-mono-data text-zinc-500">
                    <span className="flex items-center gap-1"><Ruler size={10} weight="bold" /> {l.sqm} m²</span>
                    {pricePerSqm && <span>· {formatEuro(pricePerSqm)}/m²</span>}
                    {l.rooms && <span className="flex items-center gap-1"><Bed size={10} weight="bold" /> {l.rooms}</span>}
                    {l.bathrooms && <span className="flex items-center gap-1"><Bathtub size={10} weight="bold" /> {l.bathrooms}</span>}
                  </div>
                </div>
              )}

              {/* Metadata line (when photo is present, still show sqm/rooms) */}
              {l.photo_url && (
                <div className="px-4 pt-3 pb-2 flex items-center gap-3 text-[11px] font-mono-data text-zinc-500 flex-wrap">
                  <span className="flex items-center gap-1"><Ruler size={10} weight="bold" /> {l.sqm} m²</span>
                  {pricePerSqm && <span>· {formatEuro(pricePerSqm)}/m²</span>}
                  {l.rooms && <span className="flex items-center gap-1"><Bed size={10} weight="bold" /> {l.rooms}</span>}
                  {l.bathrooms && <span className="flex items-center gap-1"><Bathtub size={10} weight="bold" /> {l.bathrooms}</span>}
                </div>
              )}

              {/* Financial mini-grid (only for sale) */}
              {isSale && f && (
                <div className="mx-4 mb-4 border border-white/5 bg-black/20 divide-y divide-white/5">
                  <div className="grid grid-cols-3 divide-x divide-white/5">
                    <MiniStat
                      icon={<Coins size={11} weight="bold" />}
                      label="CANONE"
                      value={monthlyRent ? `${formatEuro(monthlyRent)}` : "—"}
                      sub="/mese stimato"
                      valueClass="text-cyan-400"
                    />
                    <MiniStat
                      icon={<TrendUp size={11} weight="bold" />}
                      label="CAP RATE"
                      value={capPct != null ? `${capPct}%` : "—"}
                      sub="netto"
                      valueClass={yieldColor(capPct)}
                    />
                    <MiniStat
                      icon={<ChartLine size={11} weight="bold" />}
                      label="COC"
                      value={cocPct != null ? `${cocPct}%` : "—"}
                      sub="cash-on-cash"
                      valueClass={yieldColor(cocPct)}
                    />
                  </div>
                </div>
              )}

              {/* Metadata pills */}
              <div className="px-4 flex flex-wrap gap-1.5 mb-3">
                {l.energy_class && <Pill>Classe {l.energy_class}</Pill>}
                {l.condition && <Pill>{l.condition}</Pill>}
                {l.elevator && <Pill>Ascensore</Pill>}
                {l.terrace && <Pill>Terrazzo</Pill>}
                {l.garden && <Pill>Giardino</Pill>}
                {l.garage && <Pill>Box</Pill>}
              </div>

              {/* Actions */}
              <div className="mt-auto p-3 border-t border-white/5 bg-black/20 flex items-center gap-2">
                <Link
                  to="/confronto"
                  className="flex items-center gap-1 text-[11px] font-mono-data text-lime-400 hover:text-lime-300 px-2 py-1 border border-lime-400/30 hover:bg-lime-400/10 transition-colors"
                  data-testid={`fav-analyze-${l.id}`}
                >
                  ANALIZZA <ArrowRight size={10} weight="bold" />
                </Link>
                <PdfButton kind="property" id={l.id} />
                <button
                  onClick={() => onRemove(x.id)}
                  className="ml-auto text-zinc-500 hover:text-red-400 p-1.5 border border-white/5 hover:border-red-400/30 transition-colors"
                  data-testid={`fav-remove-${x.id}`}
                  title="Rimuovi"
                >
                  <Trash size={12} weight="bold" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MiniStat({ icon, label, value, sub, valueClass }) {
  return (
    <div className="p-2.5 text-center">
      <div className="flex items-center justify-center gap-1 text-zinc-500 mb-1">
        {icon}
        <span className="font-mono-data text-[9px] tracking-widest">{label}</span>
      </div>
      <div className={`font-mono-data text-sm font-bold ${valueClass}`}>{value}</div>
      <div className="font-mono-data text-[9px] text-zinc-600 mt-0.5">{sub}</div>
    </div>
  );
}

function Pill({ children }) {
  return (
    <span className="font-mono-data text-[10px] text-zinc-400 border border-white/10 px-1.5 py-0.5">{children}</span>
  );
}

function AuctionsTable({ items, onRemove }) {
  return (
    <div>
      <div className="font-mono-data text-xs text-amber-400 tracking-widest mb-3 flex items-center gap-2">
        <Gavel size={14} weight="bold" /> ASTE SALVATE
      </div>
      <div className="border border-white/10" data-testid="fav-auctions">
        {items.map((x) => {
          const a = x.snapshot;
          return (
            <div key={x.id} className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/5 hover:bg-white/[0.03] items-center font-mono-data text-sm" data-testid={`fav-auction-${a.id}`}>
              <div className="col-span-4">
                <div className="text-zinc-100">{a.type} · {a.zone}</div>
                <div className="text-[11px] text-zinc-500">{a.court} · {a.procedure_number}</div>
              </div>
              <div className="col-span-2 text-right text-amber-400 font-bold">{formatEuro(a.base_price)}</div>
              <div className="col-span-2 text-right text-zinc-400 line-through">{formatEuro(a.market_price)}</div>
              <div className="col-span-1">
                <Badge className="rounded-none bg-amber-400 text-amber-950 font-mono-data text-[10px] font-bold hover:bg-amber-400">-{a.discount_pct}%</Badge>
              </div>
              <div className="col-span-1 text-zinc-300">{a.auction_date}</div>
              <div className="col-span-2 flex items-center gap-2 justify-end">
                <PdfButton kind="auction" id={a.id} />
                <button onClick={() => onRemove(x.id)} className="text-zinc-500 hover:text-red-400 p-1" title="Rimuovi">
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
